import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { retryAfterRecentAuthentication } from '../auth/recent-auth/recent-auth-coordinator';

export interface ApiClientOptions {
  /** Returns the in-memory session token, or null when anonymous (Principle XII). */
  getToken: () => string | null;
  /** Called when an authenticated call returns 401 — the session is no longer valid. */
  onUnauthorized: () => void;
  /** Rotates the httpOnly credential and installs a fresh in-memory access token. */
  refresh?: () => Promise<boolean>;
  /**
   * Called when a MUTATION returns 403: the UI only offers policy-derived
   * actions, so a forbidden mutation means the perfil/links changed mid-session
   * (spec edge case) — claims must be re-derived. Read 403s stay friendly
   * no-access states and do NOT trigger this.
   */
  onForbiddenMutation?: () => void;
  /** Receives the X-Correlation-Id of every response (Principle XVIII). */
  onCorrelationId?: (correlationId: string) => void;
  baseUrl?: string;
  fetch?: (request: Request) => Promise<Response>;
}

export type ApiClient = Client<paths>;

// A 401 on the login endpoint means bad credentials, not an expired session.
const AUTH_PATH_PREFIX = '/v1/auth/';

export function createApiClient(options: ApiClientOptions): ApiClient {
  const performFetch = options.fetch ?? ((request: Request) => globalThis.fetch(request));
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? '/',
    // Resolve fetch lazily so runtime replacements (e.g., MSW in tests) apply
    // even though this client is created at module-import time.
    fetch: performFetch,
  });

  // Keep an untouched copy before fetch consumes the request body, so a successful refresh can
  // replay the exact original request once. Retried requests call performFetch directly and cannot
  // recursively enter this middleware.
  const retryRequests = new Map<string, Request>();
  let refreshInFlight: Promise<boolean> | null = null;

  async function refreshOnce(): Promise<boolean> {
    if (!options.refresh) return false;
    if (!refreshInFlight) {
      refreshInFlight = options.refresh().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  function captureCorrelationId(response: Response) {
    const correlationId = response.headers.get('X-Correlation-Id');
    if (correlationId) options.onCorrelationId?.(correlationId);
  }

  const middleware: Middleware = {
    onRequest({ request, id }) {
      const token = options.getToken();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      retryRequests.set(id, request.clone());
      return request;
    },
    async onResponse({ request, response, id }) {
      captureCorrelationId(response);
      const retryRequest = retryRequests.get(id);
      retryRequests.delete(id);
      const path = new URL(request.url).pathname;
      if (response.status === 428 && retryRequest && !path.startsWith(AUTH_PATH_PREFIX)) {
        const retried = await retryAfterRecentAuthentication('staff', () => performFetch(retryRequest));
        if (retried) {
          captureCorrelationId(retried);
          return retried;
        }
      }
      if (response.status === 401 && !path.startsWith(AUTH_PATH_PREFIX)) {
        if (retryRequest && (await refreshOnce())) {
          const freshToken = options.getToken();
          if (freshToken) retryRequest.headers.set('Authorization', `Bearer ${freshToken}`);
          else retryRequest.headers.delete('Authorization');

          const retried = await performFetch(retryRequest);
          captureCorrelationId(retried);
          if (retried.status === 401) options.onUnauthorized();
          if (retried.status === 403 && request.method !== 'GET') {
            options.onForbiddenMutation?.();
          }
          return retried;
        }

        options.onUnauthorized();
      }
      if (
        response.status === 403 &&
        request.method !== 'GET' &&
        !path.startsWith(AUTH_PATH_PREFIX)
      ) {
        options.onForbiddenMutation?.();
      }
      return response;
    },
  };

  client.use(middleware);
  return client;
}
